<template>
    <v-container class="secondary-font" style="font-size: 1rem;">
        <transition name="slide-from-left" mode="out-in">
            <active-players v-if="showActivePlayers" key="active-players" @setShowScreen="$emit('setShowScreen', 'live-score')" />
            <div v-else-if="current" :key="current.key">
                <team-stat-component
                    v-if="current.type === 'team' && SHOT_TYPES.includes(current.statType)"
                    :match="current.data"
                    :type="current.statType"
                />
                <compare-stat-component
                    v-else-if="current.type === 'team'"
                    :match="current.data"
                    :type="current.statType"
                    class="mt-5"
                />
                <score-run-card v-else-if="current.type === 'run' || current.type === 'drought'" :match="current.data.match" :card="current.data.card" />
                <player-stat-component
                    v-else
                    :type="current.statType"
                    :player="current.data"
                />
            </div>
        </transition>
    </v-container>
</template>

<script>
    import TeamStatComponent from '@/components/TeamStatComponent'
    import PlayerStatComponent from '@/components/PlayerStatComponent'
    import CompareStatComponent from '@/components/CompareStatComponent'
    import ActivePlayers from '@/components/ActivePlayers.vue'
    import ScoreRunCard from '@/components/ScoreRunCard'
    import matchService from '@/services/match'
    import { subscribeLiveEvents } from '@/utils/liveEvents'
    import { getClockFromLogs, hasMatchEnded } from '@/utils/matchLive'
    import { getBaskets, getCurrentRun, runStep, getLastScored, droughtMark, clockSeconds, periodOf } from '@/utils/scoreRun'

    const SHOT_TYPES = ['2P', '3P', '1P']
    // each card stays this long
    const DISPLAY_MS = 5000
    // a card that waited longer than this is out of date and skipped, so the overlay never lags the game
    const MAX_AGE_MS = 10000
    // at most this many cards wait; a burst (e.g. the stats operator correcting) keeps its latest ones
    const MAX_QUEUE = 4
    // a basket (or a correction) is followed by a fresh read of the logs, once the burst of events is over
    const LOGS_DEBOUNCE_MS = 800
    // how often the droughts are checked against the clock
    const DROUGHT_TICK_MS = 1000

    export default {
        components: {
            TeamStatComponent,
            PlayerStatComponent,
            CompareStatComponent,
            ActivePlayers,
            ScoreRunCard
        },
        props: {
            id: {
                type: String,
                required: true
            },
            showActivePlayers: {
                type: Boolean,
                default: true
            },
            // the live score is on screen and the match is on: the run and drought cards may come
            showRuns: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                SHOT_TYPES,
                // the card on screen and the ones waiting: { type, statType, data, key, receivedAt }
                current: null,
                queue: [],
                eventSeq: 0,
                advanceTimer: null,
                unsubscribe: null,
                // runs and droughts: the match (teams, logos), its baskets, the clock, and what was already shown
                match: null,
                baskets: [],
                matchEnded: false,
                clock: null,
                runShown: { team: 0, step: 0 },
                droughtShown: { 1: { since: null, mark: 0 }, 2: { since: null, mark: 0 } },
                logsLoaded: false,
                logsTimer: null,
                logsAnnounce: false,
                logsRequest: 0,
                droughtTimer: null
            }
        },
        mounted() {
            this.unsubscribe = subscribeLiveEvents(this.id, {
                player: data => this.enqueue('player', data),
                team: data => this.enqueue('team', data),
                score: data => {
                    if (data && data.colorsOnly) return
                    this.scheduleLogs(true)
                },
                timer: this.onTimer,
                // a (re)connection: what was missed meanwhile is caught up without its cards
                onOpen: () => this.scheduleLogs(false)
            })
            this.$store.dispatch('match/fetchMatch', { matchId: this.id }).then(match => {
                this.match = match
                this.loadLogs(false)
            }).catch(() => {})
            this.droughtTimer = setInterval(() => this.checkDroughts(true), DROUGHT_TICK_MS)
        },
        beforeDestroy() {
            if (this.unsubscribe) this.unsubscribe()
            clearTimeout(this.advanceTimer)
            clearTimeout(this.logsTimer)
            clearInterval(this.droughtTimer)
        },
        methods: {
            // Players: the shots only. Teams: every stat with a card
            enqueue(type, data) {
                const statType = type === 'player' ? this.getPlayerEventType(data) : this.getTeamEventType(data)
                if (type === 'player' ? !SHOT_TYPES.includes(statType) : !statType) return
                this.queue.push({ type, statType, data, key: ++this.eventSeq, receivedAt: Date.now() })
                if (this.queue.length > MAX_QUEUE) this.queue.splice(0, this.queue.length - MAX_QUEUE)
                if (!this.current) this.advance()
            },
            // The next card that is still current, each one on screen for DISPLAY_MS
            advance() {
                clearTimeout(this.advanceTimer)
                const now = Date.now()
                while (this.queue.length && now - this.queue[0].receivedAt > MAX_AGE_MS) {
                    this.queue.shift()
                }
                this.current = this.queue.shift() || null
                this.advanceTimer = this.current ? setTimeout(this.advance, DISPLAY_MS) : null
            },
            // The clock of the stats app: the game time of the droughts runs from it
            onTimer(event) {
                if (!event) return
                if (event.timerEventStatus === 'end_match') this.matchEnded = true
                const remaining = event.timer === null || event.timer === undefined || event.timer === '' ? NaN : Number(event.timer)
                if (!Number.isFinite(remaining)) return
                this.clock = {
                    period: Number(event.period) || 1,
                    remaining,
                    running: event.timerEventStatus === 'start',
                    at: Date.now()
                }
            },
            // game seconds from tip-off now: by the clock, else by the last log
            gameSeconds() {
                const clock = this.clock
                if (clock) {
                    const remaining = clock.running ? clock.remaining - (Date.now() - clock.at) : clock.remaining
                    return clockSeconds(clock.period, remaining)
                }
                const last = this.baskets[this.baskets.length - 1]
                return last ? last.at : 0
            },
            scheduleLogs(announce) {
                clearTimeout(this.logsTimer)
                // a pending read that would announce keeps doing so
                this.logsAnnounce = announce || this.logsAnnounce
                this.logsTimer = setTimeout(() => {
                    const value = this.logsAnnounce
                    this.logsAnnounce = false
                    this.loadLogs(value)
                }, LOGS_DEBOUNCE_MS)
            },
            loadLogs(announce) {
                if (!this.match || !this.match.team1 || !this.match.team2) return
                const request = ++this.logsRequest
                matchService.getMatchLogs(this.id).then(resp => {
                    if (request !== this.logsRequest) return
                    const logs = Array.isArray(resp.data) ? resp.data : []
                    this.matchEnded = hasMatchEnded(logs)
                    this.baskets = getBaskets(logs, this.match.team1.id, this.match.team2.id)
                    this.logsLoaded = true
                    // before any clock event: where the clock stands by the logs
                    if (!this.clock) {
                        const clock = getClockFromLogs(logs)
                        if (clock) this.clock = { period: clock.period, remaining: clock.timer, running: false, at: Date.now() }
                    }
                    this.checkRun(announce)
                    this.checkDroughts(announce)
                }).catch(() => {
                    // the next basket brings another try
                })
            },
            canAnnounce() {
                return this.showRuns && !this.matchEnded && !!this.match
            },
            // the run card: when a run reaches 8 points, and each time it grows by 4 more
            checkRun(announce) {
                const run = getCurrentRun(this.baskets)
                if (!run) {
                    this.runShown = { team: 0, step: 0 }
                    return
                }
                const step = runStep(run)
                const shown = this.runShown.team === run.team ? this.runShown.step : 0
                if (step <= shown) return
                this.runShown = { team: run.team, step }
                if (!announce || !this.canAnnounce()) return
                const now = Math.max(this.gameSeconds(), this.baskets[this.baskets.length - 1].at)
                const fromPeriod = periodOf(run.startAt)
                this.enqueueCard('run', {
                    kind: 'run',
                    team: run.team,
                    points: run.points,
                    against: run.against,
                    seconds: now - run.startAt,
                    fromPeriod: fromPeriod < periodOf(now) ? fromPeriod : null
                })
            },
            // the drought card: a team 3 and 5 minutes of game time without a point (not while the other team
            // is on a run: that card says it already)
            checkDroughts(announce) {
                if (!this.match || !this.logsLoaded) return
                const now = this.gameSeconds()
                const last = getLastScored(this.baskets)
                const run = getCurrentRun(this.baskets)
                ;[1, 2].forEach(team => {
                    const since = last[team] === null ? 0 : last[team]
                    const shown = this.droughtShown[team]
                    // a new basket of the team: its drought starts over
                    if (shown.since !== since) {
                        this.droughtShown[team] = { since, mark: announce ? 0 : droughtMark(now - since) }
                        if (!announce) return
                    }
                    const mark = droughtMark(now - since)
                    if (mark <= this.droughtShown[team].mark) return
                    this.droughtShown[team].mark = mark
                    if (!announce || !this.canAnnounce() || (run && run.team !== team)) return
                    this.enqueueCard('drought', { kind: 'drought', team, seconds: mark, scored: last[team] !== null })
                })
            },
            enqueueCard(type, card) {
                this.queue.push({ type, statType: type, data: { match: this.match, card }, key: ++this.eventSeq, receivedAt: Date.now() })
                if (this.queue.length > MAX_QUEUE) this.queue.splice(0, this.queue.length - MAX_QUEUE)
                if (!this.current) this.advance()
            },
            getTeamEventType(teamEvent) {
                if (!teamEvent) return ''
                if (teamEvent.team1.twoPointsMade > 0 || teamEvent.team1.twoPointAttempts > 0 || teamEvent.team2.twoPointsMade > 0 || teamEvent.team2.twoPointAttempts > 0) {
                    return '2P'
                } else if (teamEvent.team1.threePointsMade > 0 || teamEvent.team1.threePointAttempts > 0 || teamEvent.team2.threePointsMade > 0 || teamEvent.team2.threePointAttempts > 0) {
                    return '3P'
                } else if (teamEvent.team1.freeThrowsMade > 0 || teamEvent.team1.freeThrowAttempts > 0 || teamEvent.team2.freeThrowsMade > 0 || teamEvent.team2.freeThrowAttempts > 0) {
                    return '1P'
                } else if (teamEvent.team1.offensiveRebounds > 0 || teamEvent.team2.offensiveRebounds > 0) {
                    return 'T.R.'
                } else if (teamEvent.team1.defensiveRebounds > 0 || teamEvent.team2.defensiveRebounds > 0) {
                    return 'T.R.'
                } else if (teamEvent.team1.assists > 0 || teamEvent.team2.assists > 0) {
                    return 'AST'
                } else if (teamEvent.team1.steals > 0 || teamEvent.team2.steals > 0) {
                    return 'STL'
                } else if (teamEvent.team1.turnovers > 0 || teamEvent.team2.turnovers > 0) {
                    return 'TO'
                } else if (teamEvent.team1.blockedShots > 0 || teamEvent.team2.blockedShots > 0) {
                    return 'BLK'
                }
                return ''
            },
            getPlayerEventType(playerEvent) {
                if (!playerEvent) return ''
                if (playerEvent.twoPointsMade > 0 || playerEvent.twoPointAttempts > 0) {
                    return '2P'
                } else if (playerEvent.threePointsMade > 0 || playerEvent.threePointAttempts > 0) {
                    return '3P'
                } else if (playerEvent.freeThrowsMade > 0 || playerEvent.freeThrowAttempts > 0) {
                    return '1P'
                } else if (playerEvent.rebounds > 0) {
                    return 'rebounds'
                } else if (playerEvent.assists > 0) {
                    return 'assists'
                } else if (playerEvent.steals > 0) {
                    return 'steals'
                } else if (playerEvent.turnovers > 0) {
                    return 'turnovers'
                } else if (playerEvent.blockedShots > 0) {
                    return 'blockedShots'
                }
                return ''
            }
        }
    }
</script>

<style>
    .v-progress-circular__info {
        display: block;
    }
</style>
